/*
 * Created by Anup Shrestha (anup.stha012@gmail.com)
 * Copyright (c) 2022.  All rights reserved.
 * Last modified 5/1/22, 3:54 PM
 *
 */

import { useEffect, useRef, useState } from 'react';
import LetteredAvatar from 'react-avatar';
import { Helmet } from 'react-helmet';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useReactToPrint } from 'react-to-print';
import groupBy from 'lodash/groupBy';
import { WarningOctagon } from 'phosphor-react';
import { useAppSelector } from 'stores';

import { Member } from 'features/members/types';

import { privateAgent } from 'config/axios';

import CoverBg from 'assets/img/cover-bg.svg';

import Button from 'components/Button';
import Input from 'components/Input';
import Loader from 'components/Loader';
import { TableView } from 'components/Table';
import { ProfileAllTestPrint } from 'features/members/components/others/ProfileAllTestPrint/ProfileAllTestPrint';
import { ProfileTestTableRow } from 'features/members/components/table/ProfileTestTableRow';

interface IMemberTest {
  current_member: Member;
}

export const TestReportPage = ({ current_member }: IMemberTest) => {
  const now = new Date();

  const [subTestGroups, setSubTestGroups] = useState<any>({});
  const [data, setData] = useState<any>();
  const [error, setError] = useState('');
  const [isFetching, setIsFetching] = useState(false);
  now.setMinutes(now.getMinutes() - now.getTimezoneOffset());

  const getFiscalYear = () => {
    const year = now.getFullYear();
    const newDate = new Date(`${year}/04/14`);
    return newDate.toISOString().slice(0, 16);
  };

  const { handleSubmit, register } = useForm({
    defaultValues: {
      start_date: getFiscalYear(),
      end_date: now.toISOString().slice(0, 16),
    },
  });

  useEffect(() => {
    if (!isFetching && data) {
      const subTestDetails =
        data?.data && Object.keys(data.data.data).length !== 0
          ? data.data.data.list.map((element: any) => {
              return Object.assign(
                {},
                {
                  test_date: element.test_date,
                  test_name: element.test_name,
                  app_slug: element.app_slug,

                  tests: element.report.map((sub: any) => ({
                    [sub.name]: sub.value,
                    [`Note`]: sub.note,
                    unit: sub.unit,
                  })),
                }
              );
            })
          : [];
      const res = groupBy(subTestDetails, 'test_name');
      setSubTestGroups(res);
    }
  }, [data, isFetching]);

  const componentRef = useRef<HTMLDivElement>(null);
  const handlePrint = useReactToPrint({ content: () => componentRef.current });

  if (current_member && Object.keys(current_member).length === 0) {
    return <Loader />;
  }

  return (
    <>
      <Helmet>
        <title>Test Report | {current_member.name}</title>
      </Helmet>
      {current_member && (
        <div className="hidden">
          <ProfileAllTestPrint
            test_name="Test Report"
            test={subTestGroups}
            member={current_member}
            ref={componentRef}
          />
        </div>
      )}

      <div className="overflow-visible px-10 py-10 sm:p-6">
        <div className="relative w-full overflow-hidden rounded-xl bg-white ring-1 ring-black ring-opacity-10 print:hidden sm:w-full">
          <img src={CoverBg} className="z-0 h-52  w-full object-cover" alt="cover image" />

          <div className="absolute left-[1.7%] top-40 z-0 flex items-center gap-x-6">
            <div className="rounded-full ring-4 ring-white">
              <LetteredAvatar name={current_member?.name} size="120" round={true} maxInitials={2} />
            </div>
            <div className="mt-10 flex flex-col">
              <h1 className="text-3xl font-medium capitalize tracking-wider text-gray-900 sm:text-3xl">
                {current_member?.name}
              </h1>
              <p className="text-xl font-medium text-gray-500 sm:text-lg">
                {current_member?.email}
              </p>
            </div>
          </div>
          <div className="mt-36 space-y-6 py-4 px-8">
            <div className="flex flex-col">
              <h1 className="text-3xl font-medium capitalize tracking-wider text-gray-700 sm:text-3xl">
                Patient Test Report
              </h1>
              <p className="text-xl font-medium text-gray-500 sm:text-lg">
                Please choose start date and end date to generate report.
              </p>
            </div>

            <hr />

            <div className="flex flex-col space-y-4">
              <div className="flex items-end justify-between">
                <form
                  onSubmit={handleSubmit(async (values) => {
                    if (!values.start_date || !values.end_date) {
                      return toast.error('Please select both start date or end date');
                    }
                    setData({});
                    setSubTestGroups({});
                    setIsFetching(true);
                    setError('');
                    try {
                      const start_date = +new Date(values.start_date);

                      localStorage.setItem('start_date', `${start_date}`);
                      const end_date = +new Date(values.end_date);
                      const res = await privateAgent.get(
                        `test/report?from_time_stamp=${start_date / 1000}&to_time_stamp=${
                          end_date / 1000
                        }&member_id=${current_member.id}`
                      );
                      setData(res);
                      setIsFetching(false);
                    } catch (error) {
                      setIsFetching(false);
                      setError(error);
                    }
                  })}
                  className="flex items-end space-x-4"
                >
                  <Input
                    label="Enter Start Date"
                    type="datetime-local"
                    required={false}
                    max={now.toISOString().slice(0, 16)}
                    {...register('start_date')}
                  />
                  <Input
                    label="Enter End Date"
                    type="datetime-local"
                    required={false}
                    max={now.toISOString().slice(0, 16)}
                    {...register('end_date')}
                  />
                  <Button>Search</Button>
                </form>
                {!error && data && (
                  <div className="flex space-x-4">
                    <Button onClick={handlePrint}>Print Report</Button>
                  </div>
                )}
              </div>
            </div>

            {(error || !data) && (
              <div className="flex items-center space-x-2 text-xl font-medium text-red-400 ">
                <WarningOctagon size={24} /> <span>No Details Found</span>
              </div>
            )}

            <div className="flex flex-col space-y-4">
              {!isFetching ? (
                Object.keys(subTestGroups).map((element) => (
                  <div className="flex flex-col space-y-2" key={element}>
                    <TableView
                      data={subTestGroups[element]}
                      tableHeadings={[
                        `${element} Test Date`,
                        'Temperature',
                        `${element} Test Result`,
                      ]}
                      search={false}
                      tableRowComponent={<ProfileTestTableRow />}
                      loading={isFetching}
                      paginate={false}
                    />
                  </div>
                ))
              ) : (
                <Loader />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export const MemberTestReportPage = () => {
  const current_member = useAppSelector((state: any) => state.members.member);

  return current_member ? <TestReportPage current_member={current_member} /> : null;
};
